import { ImportedListing } from '../../_types/imported-listing'
import { ListingTag } from '../../_types/listing-tag'
import { ApplicationParagraphsResult } from '../../_types/quickpass-llm'
import { QuickPassProfile } from '../../_types/quickpass-profile'
import {
  ApplicationParagraphsState,
  CachedApplicationParagraphs,
  ParagraphSection,
} from './types'

const CACHE_PREFIX = 'quickpass.applicationParagraphs:v1:'
const CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000

const PARAGRAPH_SECTIONS: ParagraphSection[] = [
  {
    key: 'aboutMe',
    label: 'About me',
    description: 'Quick personal intro — who you are and what you do.',
  },
  {
    key: 'whyImAGoodFit',
    label: "Why I'm a good fit",
    description:
      'The pitch a landlord wants — reliability, respect for the place, financial stability.',
  },
  {
    key: 'additionalNotes',
    label: 'Additional notes',
    description:
      'Anything else worth flagging — pets, move-in date, references on request.',
  },
]

const buildProfileFingerprint = (profile: QuickPassProfile): string => {
  const preferences = profile.unitPreferences
  return [
    profile.firstName,
    profile.lastName,
    preferences.cities.slice().sort().join('|'),
    preferences.priceMin ?? 'np-min',
    preferences.priceMax ?? 'np-max',
    preferences.bedroomCounts.slice().sort().join(','),
    preferences.dwellingScope,
    preferences.preferredMoveInDate ?? 'np-date',
    profile.monthlyTakeHomeIncome ?? 'np-income',
  ].join('::')
}

/**
 * Builds a small profile projection for the LLM. Strips out IDs,
 * timestamps, and the noisy collections — keeps the human details a
 * landlord would care about.
 *
 * @param {QuickPassProfile} profile - the renter profile
 * @returns {Record<string, unknown>} the LLM-ready projection
 */
const buildProfileForPrompt = (
  profile: QuickPassProfile,
): Record<string, unknown> => {
  const preferences = profile.unitPreferences

  return {
    firstName: profile.firstName,
    lastName: profile.lastName,
    cities: preferences.cities,
    priceMin: preferences.priceMin,
    priceMax: preferences.priceMax,
    bedroomCounts: preferences.bedroomCounts,
    bathroomCounts: preferences.bathroomCounts,
    propertyTypes: preferences.propertyTypes,
    dwellingScope: preferences.dwellingScope,
    preferredMoveInDate: preferences.preferredMoveInDate,
    monthlyTakeHomeIncome: profile.monthlyTakeHomeIncome,
  }
}

/**
 * Builds a short text summary the LLM uses as supplementary context —
 * how engaged the renter has been (listings tracked, tags applied) so
 * the paragraphs sound grounded.
 *
 * @param {ImportedListing[]} listings
 * @param {Record<string, ListingTag>} tags
 * @returns {string} the short summary
 */
const buildListingsContextSummary = (
  listings: ImportedListing[],
  tags: Record<string, ListingTag>,
): string => {
  if (listings.length === 0) {
    return 'No tracked listings yet.'
  }

  const yesCount = Object.values(tags).filter(value => value === 'yes').length
  const cities = Array.from(
    new Set(
      listings.map(listing => listing.city).filter(value => value !== ''),
    ),
  )

  return `Tracking ${listings.length} ${listings.length === 1 ? 'listing' : 'listings'} across ${cities.join(', ') || 'multiple cities'}, with ${yesCount} marked as a "yes" so far.`
}

const readCache = (
  userId: string,
  fingerprint: string,
): CachedApplicationParagraphs | null => {
  try {
    const raw = localStorage.getItem(`${CACHE_PREFIX}${userId}`)
    if (raw === null) {
      return null
    }
    const parsed = JSON.parse(raw) as CachedApplicationParagraphs
    if (parsed.fingerprint !== fingerprint) {
      return null
    }
    if (Date.now() - parsed.generatedAt > CACHE_TTL_MS) {
      return null
    }
    return parsed
  } catch (error) {
    console.error('[application-paragraphs] cache read failed', error)
    return null
  }
}

const writeCache = (
  userId: string,
  fingerprint: string,
  result: ApplicationParagraphsResult,
): void => {
  try {
    const entry: CachedApplicationParagraphs = {
      fingerprint,
      result,
      generatedAt: Date.now(),
    }
    localStorage.setItem(`${CACHE_PREFIX}${userId}`, JSON.stringify(entry))
  } catch (error) {
    console.error('[application-paragraphs] cache write failed', error)
  }
}

const clearCache = (userId: string): void => {
  try {
    localStorage.removeItem(`${CACHE_PREFIX}${userId}`)
  } catch (error) {
    console.error('[application-paragraphs] cache clear failed', error)
  }
}

const generateButtonLabelFor = (state: ApplicationParagraphsState): string => {
  if (state === 'loading') {
    return 'Drafting…'
  }
  if (state === 'ready') {
    return 'Re-draft'
  }
  return 'Generate copy'
}

export {
  PARAGRAPH_SECTIONS,
  buildProfileFingerprint,
  buildProfileForPrompt,
  buildListingsContextSummary,
  readCache,
  writeCache,
  clearCache,
  generateButtonLabelFor,
}
